import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState } from "react";

const DIALOG_CONFIG = {
  approve: {
    title: "Approve review?",
    text: "This review will be approved.",
    confirmLabel: "Approve",
    confirmClass: "btn btn-success btn-sm",
    needsReason: false,
  },
  remove: {
    title: "Remove review?",
    text: "",
    confirmLabel: "Remove review",
    confirmClass: "btn btn-danger btn-sm",
    needsReason: true,
  },
  restore: {
    title: "Restore review?",
    text: "This review will be visible on the public garage profile again.",
    confirmLabel: "Restore",
    confirmClass: "btn btn-success btn-sm",
    needsReason: false,
  },
};

/**
 * Generic moderation dialog for approve / remove / restore.
 *
 * Props:
 *  - isOpen    boolean
 *  - type      "approve" | "remove" | "restore"
 *  - reasons   [{ value, label }]  (used by "remove")
 *  - loading   boolean
 *  - onClose   () => void
 *  - onConfirm ({ reason, note }) => void
 */
const ReviewActionDialog = ({
  isOpen,
  type = "approve",
  reasons = [],
  loading = false,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  const config = DIALOG_CONFIG[type] || DIALOG_CONFIG.approve;

  useEffect(() => {
    if (isOpen) {
      setReason("");
      setNote("");
      setError("");
    }
  }, [isOpen, type]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (config.needsReason && !reason) {
      setError("Please select a removal reason.");
      return;
    }

    onConfirm({ reason, note: note.trim() });
  };

  return (
    <>
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        onClick={() => !loading && onClose()}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          role="document"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content radius-12">
            <div className="modal-header border-bottom py-16 px-24">
              <h6 className="modal-title mb-0">{config.title}</h6>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onClose}
                disabled={loading}
              />
            </div>

            <div className="modal-body p-24 text-start">
              {config.text && (
                <p className="text-secondary-light mb-3">{config.text}</p>
              )}

              {config.needsReason && (
                <div className="mb-3">
                  <label htmlFor="review-removal-reason" className="form-label">
                    Removal reason <span className="text-danger">*</span>
                  </label>

                  <div className="position-relative">
                    <select
                      id="review-removal-reason"
                      className="form-select"
                      style={{
                        appearance: "none",
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        backgroundImage: "none",
                        paddingRight: "40px",
                      }}
                      value={reason}
                      onChange={(e) => {
                        setReason(e.target.value);
                        setError("");
                      }}
                    >
                      <option value="">Select a reason</option>
                      {reasons.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>

                    <Icon
                      icon="mdi:chevron-down"
                      className="menu-icon"
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: "16px",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        fontSize: "14px",
                      }}
                    />
                  </div>

                  {error && (
                    <small className="text-danger d-block mt-1">{error}</small>
                  )}
                </div>
              )}

              <label htmlFor="review-action-note" className="form-label">
                Admin note (optional)
              </label>
              <textarea
                id="review-action-note"
                className="form-control"
                rows="3"
                placeholder="Enter an internal note..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            <div className="modal-footer border-top py-16 px-24 gap-2">
              <button
                type="button"
                className="btn btn-sm"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className={config.confirmClass}
                onClick={handleConfirm}
                disabled={loading}
              >
                {loading ? "Please wait..." : config.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
};

export default ReviewActionDialog;
